import { useState } from 'react';
import { Lock, Mail, CircleAlert as AlertCircle, Loader as Loader2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { MosLockup } from './brand/MosMark';

const FORGOT_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e3a97ae5`;

export function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Forgot-password modal state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    try {
      // Always 200, never leaks whether the address exists. Show the same
      // "if an account exists you'll get an email" message regardless.
      await fetch(`${FORGOT_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      setForgotSent(true);
    } catch (err) {
      // Even on network error, show the silent-success message — we don't
      // want to reveal anything about backend state.
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      toast.success('Welcome back!');
    } catch (err: any) {
      console.error('Login error:', err);
      // Supabase's "Request rate limit reached" is a per-office-IP limiter,
      // not a wrong password — retrying immediately keeps the limiter hot.
      const raw: string = err?.message || '';
      const friendly = /rate limit/i.test(raw)
        ? 'Too many sign-in attempts from this location. Wait a minute or two, then try again — your password is fine.'
        : raw || 'Failed to sign in. Please check your credentials.';
      setError(friendly);
      toast.error(friendly);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: 'Inter, Helvetica, sans-serif',
    backgroundColor: '#ffffff',
    color: '#2c2a29',
    border: '1px solid #d1d5dc',
    borderRadius: '10px',
    height: '50px',
    width: '100%',
    paddingLeft: '40px',
    paddingRight: '12px',
    fontSize: '16px',
    outline: 'none',
    boxShadow: 'none',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', padding: '16px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '448px' }}>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', marginBottom: '32px' }}>
          <MosLockup height={60} style={{ marginBottom: '20px' }} />

          <h1 style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 700, color: '#101828', fontSize: '30px', lineHeight: '36px', textAlign: 'center', margin: '0 0 8px 0' }}>
            Marcstone OS
          </h1>

          <p style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 400, color: '#4a5565', fontSize: '16px', lineHeight: '24px', textAlign: 'center', margin: 0 }}>
            Sign in to your account
          </p>
        </div>

        <div style={{ width: '100%', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0px 8px 10px -6px rgba(0,0,0,0.1), 0px 20px 25px -5px rgba(0,0,0,0.1)' }}>
          <div style={{ padding: '33px' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label
                  htmlFor="email"
                  style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 500, color: '#364153', fontSize: '14px', lineHeight: '20px' }}
                >
                  Email Address
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', display: 'flex', alignItems: 'center', pointerEvents: 'none', zIndex: 1 }}>
                    <Mail style={{ width: '16px', height: '16px' }} />
                  </span>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    disabled={loading}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label
                  htmlFor="password"
                  style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 500, color: '#364153', fontSize: '14px', lineHeight: '20px' }}
                >
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', display: 'flex', alignItems: 'center', pointerEvents: 'none', zIndex: 1 }}>
                    <Lock style={{ width: '16px', height: '16px' }} />
                  </span>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter your password"
                    disabled={loading}
                    style={inputStyle}
                  />
                </div>
              </div>

              {error && (
                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <AlertCircle style={{ width: '20px', height: '20px', color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
                  <p style={{ fontSize: '14px', color: '#991b1b', margin: 0 }}>{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  height: '48px',
                  width: '100%',
                  backgroundColor: loading ? 'rgba(44,42,41,0.5)' : '#2c2a29',
                  borderRadius: '10px',
                  fontFamily: 'Inter, Helvetica, sans-serif',
                  fontWeight: 500,
                  color: '#ffffff',
                  fontSize: '16px',
                  lineHeight: '24px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => { if (!loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#3d3b3a'; }}
                onMouseLeave={(e) => { if (!loading) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#2c2a29'; }}
              >
                {loading ? (
                  <>
                    <Loader2 style={{ width: '20px', height: '20px' }} className="animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '24px', paddingTop: '24px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => { setForgotOpen(true); setForgotEmail(email); setForgotSent(false); }}
                style={{
                  fontFamily: 'Inter, Helvetica, sans-serif',
                  fontWeight: 500,
                  color: '#F0533E',
                  fontSize: '14px',
                  lineHeight: '20px',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                }}
              >
                Forgot password?
              </button>
            </div>
          </div>
        </div>

        <p style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 400, color: '#6a7282', fontSize: '14px', lineHeight: '20px', textAlign: 'center', marginTop: '20px' }}>
          Need help? Contact your system administrator
        </p>
      </div>

      {/* Forgot-password modal */}
      {forgotOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={() => setForgotOpen(false)} style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />
          <div style={{ position: 'relative', width: '100%', maxWidth: '420px', backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 600, color: '#111827', fontSize: '18px', margin: 0 }}>Reset password</h3>
              <button onClick={() => setForgotOpen(false)} style={{ padding: '6px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: '6px' }}>
                <X style={{ width: '16px', height: '16px', color: '#6b7280' }} />
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              {forgotSent ? (
                <div>
                  <p style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontSize: '14px', color: '#374151', lineHeight: '20px', margin: 0 }}>
                    If an account exists for that email, a password-reset link is on its way. Check your inbox (and spam folder) — the link is good for 24 hours.
                  </p>
                  <button
                    onClick={() => setForgotOpen(false)}
                    style={{ marginTop: '20px', width: '100%', height: '44px', backgroundColor: '#2c2a29', color: '#fff', border: 'none', borderRadius: '10px', fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 500, fontSize: '14px', cursor: 'pointer' }}
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgotSubmit}>
                  <p style={{ fontFamily: 'Inter, Helvetica, sans-serif', fontSize: '13px', color: '#6b7280', lineHeight: '18px', margin: '0 0 16px 0' }}>
                    Enter your email and we'll send a link to set a new password.
                  </p>
                  <label htmlFor="forgot-email" style={{ display: 'block', fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 500, color: '#364153', fontSize: '13px', marginBottom: '6px' }}>Email Address</label>
                  <input
                    id="forgot-email"
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@marcstone.com"
                    style={{
                      ...inputStyle,
                      paddingLeft: '12px',
                      height: '44px',
                    }}
                    disabled={forgotLoading}
                  />
                  <button
                    type="submit"
                    disabled={forgotLoading || !forgotEmail.trim()}
                    style={{ marginTop: '16px', width: '100%', height: '44px', backgroundColor: forgotLoading ? '#9ca3af' : '#F0533E', color: '#fff', border: 'none', borderRadius: '10px', fontFamily: 'Inter, Helvetica, sans-serif', fontWeight: 500, fontSize: '14px', cursor: forgotLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    {forgotLoading ? <Loader2 style={{ width: '16px', height: '16px' }} className="animate-spin" /> : null}
                    {forgotLoading ? 'Sending...' : 'Send reset link'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
